import '@ui5/webcomponents-icons/dist/search';
import { Ui5CustomEvent, VariantItemPropTypes } from '@ui5/webcomponents-react';
import { SelectedVariant } from '@ui5/webcomponents-react/dist/internal/VariantManagementContext';
import {
  BaseQueryFilters,
  DistributionStatus,
  IssueType,
  Maybe,
  SearchFilterDelete,
  SearchFilterDetailsFragment,
  SearchFilterFieldDetailsFragment,
  SearchFilterUpdate,
  Status,
  TranslationType,
  useCreateSearchFilterMutation,
  useGetAllProjectNamesQuery,
  useGetSearchFiltersQuery,
  useUpdateSearchFilterMutation,
  useUpdateSearchFiltersMutation
} from 'generated/graphql';
import { useEffect, useState } from 'react';
import { FieldErrorsImpl, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { defaultFiltersVisibility, useAdaptFiltersStore } from 'stores/AdaptFiltersStore';
import { FiltersVisibility, FiltersStateOnOpenDialog } from 'stores/interfaces/AdaptFiltersState';
import { joiResolver } from '@hookform/resolvers/joi';
import Joi from 'joi';
import { isArray, isEqual } from 'lodash';
import { formatDateRange } from 'hooks/useGetDate';
import { getColumnConfig, getStandardView, STANDARD_VIEW_ID } from './StandardView';
import { useSearchColumnsStore } from 'stores/SearchColumnsStore';
import { getSearchColumnVisibility } from 'utils/searchColumnVisibility';
import { useUserViewStore } from 'stores/UserViewStore';
import { usePrevious } from 'hooks/usePrevious';

type UseFiltersProps = {
  onFilter: (filters: BaseQueryFilters, isUserInteraction?: boolean) => void;
  internal: boolean;
};

type BaseQueryFiltersKeys = keyof BaseQueryFilters;

export const eventFilterKeys = {
  search: '.$text-search-group',
  project: '.$project-group',
  targetLanguage: '.$target-language-group',
  status: '.$status-group',
  createdBy: '.$created-by-group',
  qualityExpert: '.$quality-expert-group',
  projectManager: '.$project-manager-group',
  queryResponsible: '.$query-responsible-group',
  assignee: '.$assignee-group',
  answerer: '.$answerer-group',
  translationType: '.$translation-type-group',
  xtmProjectName: '.$xtm-project-name-group',
  filename: '.$filename-group',
  submitterGroupName: '.$submitter-group-name-group',
  loio: '.$loio-group',
  repository: '.$repository-group',
  collection: '.$collection-group',
  stringId: '.$string-id-group',
  distributed: '.$distributed-group',
  createdAt: '.$createdAt-group',
  queryTrackerId: '.$query-tracker-id-group',
  implementationStatus: '.$implementation-status-group',
  implementationLanguage: '.$implementation-language-group',
  account: '.$account-group',
  applicationComponent: '.$application-component-group',
  substitutePm: '.$substitute-pm-group',
  issueType: '.$issue-type-group',
  sourceContact: '.$source-contact-group',
  objectName: '.$object-name-group',
  objectType: '.$object-type-group',
  translationArea: '.$translation-area-group'
};

export const filtersMinLength = 3;

const schema = Joi.object<BaseQueryFilters>({
  qualityExpert: Joi.string().allow('').min(filtersMinLength),
  projectManager: Joi.string().allow('').min(filtersMinLength),
  queryResponsible: Joi.string().allow('').min(filtersMinLength),
  assignee: Joi.string().allow('').min(filtersMinLength),
  answerer: Joi.string().allow('').min(filtersMinLength),
  substitutePm: Joi.string().allow('').min(filtersMinLength),
  createdBy: Joi.string().allow('').min(filtersMinLength),
  sourceContact: Joi.string().allow('').min(filtersMinLength)
}).unknown();

export const useFilters = (props: UseFiltersProps) => {
  const { onFilter, internal } = props;
  const { t } = useTranslation();

  const notSelected = t('display-query-filters-not-selected');
  const allOptions = t('display-query-filters-all-options');

  type FieldValuesBaseOverride = keyof Pick<
    BaseQueryFilters,
    | 'translationType'
    | 'status'
    | 'distributed'
    | 'implementationStatus'
    | 'implementationLanguage'
    | 'issueType'
  >;
  type FieldValuesBase = Record<Exclude<BaseQueryFiltersKeys, FieldValuesBaseOverride>, string>;
  interface FilterValues extends FieldValuesBase, Record<FieldValuesBaseOverride, any> {
    translationType: TranslationType | typeof notSelected;
    distributed: boolean;
    status: string[];
    implementationStatus: string[];
    implementationLanguage: string[];
    issueType: string[];
  }

  const defaultFilterValues: FilterValues = {
    search: '',
    project: '',
    targetLanguage: allOptions,
    status: [],
    createdBy: '',
    qualityExpert: '',
    projectManager: '',
    queryResponsible: '',
    assignee: '',
    answerer: '',
    translationType: notSelected,
    xtmProjectName: '',
    filename: '',
    submitterGroupName: '',
    loio: '',
    repository: '',
    collection: '',
    stringId: '',
    distributed: false,
    queryTrackerId: '',
    createdAt: '',
    implementationStatus: [],
    implementationLanguage: [],
    account: '',
    applicationComponent: '',
    substitutePm: '',
    issueType: [],
    sourceContact: '',
    objectName: '',
    objectType: '',
    translationArea: ''
  };

  const form = useForm({
    reValidateMode: 'onChange',
    mode: 'onChange',
    defaultValues: defaultFilterValues,
    resolver: joiResolver(schema)
  });

  const isInternalUserOnly: Record<BaseQueryFiltersKeys, boolean> = {
    search: false,
    project: false,
    targetLanguage: false,
    status: false,
    createdBy: false,
    qualityExpert: true,
    projectManager: true,
    queryResponsible: true,
    assignee: true,
    answerer: true,
    translationType: false,
    xtmProjectName: false,
    filename: false,
    submitterGroupName: false,
    loio: false,
    repository: false,
    collection: false,
    stringId: false,
    distributed: false,
    queryTrackerId: false,
    createdAt: false,
    implementationStatus: true,
    implementationLanguage: false,
    applicationComponent: true,
    account: true,
    substitutePm: true,
    issueType: false,
    sourceContact: false,
    objectName: false,
    objectType: false,
    translationArea: false
  };

  const [focus, setFocus] = useState<undefined | string>();
  const clearFocus = () => setFocus(undefined);
  const [isSearchRequired, setIsSearchRequired] = useState(false);
  const [isFilterVisibilityDirty, setFilterVisibilityDirty] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<SearchFilterDetailsFragment | null>(null);

  const { filtersVisibility, setFiltersVisibility } = useAdaptFiltersStore();
  const setSearchColumnsVisibility = useSearchColumnsStore(
    (state) => state.setSearchColumnsVisibility
  );
  const searchColumnsVisibility = useSearchColumnsStore((state) => state.searchColumnsVisibility);
  const setHasChangedColumnVisibility = useSearchColumnsStore(
    (state) => state.setHasChangedColumnVisibility
  );
  const hasChangedColumnVisibility = useSearchColumnsStore(
    (state) => state.hasChangedColumnVisibility
  );

  const {
    sessionVariant,
    setSessionVariant,
    shouldUseSessionVariant,
    setShouldUseSessionVariant,
    dirtyState,
    setDirtyState,
    defaultViewPreferenceId,
    setDefaultViewPreferenceId,
    setViews
  } = useUserViewStore();

  const [{ fetching: fetchingSearchQueries, data }] = useGetSearchFiltersQuery({
    requestPolicy: 'cache-and-network'
  });

  const searchFilters = data?.searchFilters;
  const [, callCreateSearchFilter] = useCreateSearchFilterMutation();
  const [, callUpdateMultipleSearchFilters] = useUpdateSearchFiltersMutation();
  const [, callUpdateOneSearchFilters] = useUpdateSearchFilterMutation();
  const [projectNames] = useGetAllProjectNamesQuery();

  const [openDialogFilters, setOpenDialogFilters] = useState<FiltersStateOnOpenDialog>({
    ...defaultFilterValues
  });

  const getFilterValue = (filter: string, value?: Maybe<string>) => {
    if (
      filter === 'status' ||
      filter === 'implementationStatus' ||
      filter === 'implementationLanguage' ||
      filter === 'issueType'
    ) {
      return value?.split(',') || [];
    }
    if (filter === 'distributed') {
      return value === 'true' ? true : false;
    }
    return value || '';
  };

  const addStandardViewIfAbsent = (standardView: SearchFilterDetailsFragment) => {
    if (searchFilters) {
      const hasStandardView = searchFilters.some((filter) => filter.id === STANDARD_VIEW_ID);
      if (!hasStandardView) {
        searchFilters.unshift(standardView);
      }
    }
  };

  const clearFiltersFragmentObject = (searchFiltersFragment?: SearchFilterDetailsFragment[]) => {
    searchFiltersFragment?.forEach((searchFilter) => {
      searchFilter.filters.forEach((filter) => delete filter.__typename);
    });
  };

  const removeDefaultViewPreferenceDuplicates = () => {
    if (!searchFilters || !searchFilters.length) {
      return;
    }

    const lastViewOption = searchFilters[searchFilters.length - 1];
    // should consider the most recent (last) view when created with the 'set as default' option or use the last selected default view preference
    const updatedDefaultViewPreferenceId = lastViewOption.isDefault
      ? lastViewOption.id
      : defaultViewPreferenceId;

    searchFilters.forEach((sf) => {
      if (sf.id === updatedDefaultViewPreferenceId) sf.isDefault = true;
      else sf.isDefault = false;
    });
  };

  useEffect(() => {
    if (selectedVariant && sessionVariant !== selectedVariant) setSessionVariant(selectedVariant);
  }, [selectedVariant]);

  useEffect(() => {
    if (searchFilters) {
      setViews(searchFilters);
    }

    clearFiltersFragmentObject(searchFilters);

    const standardView = getStandardView(internal);
    addStandardViewIfAbsent(standardView);

    if (!searchFilters || !searchFilters.length) {
      return;
    }

    if (!shouldUseSessionVariant) removeDefaultViewPreferenceDuplicates();

    const selectedVariant = getSessionVariantOrDefaultView();
    setSelectedVariant(selectedVariant);
    setShouldUseSessionVariant(false);

    if (selectedVariant.columnConfig) {
      const columns = getSearchColumnVisibility(selectedVariant.columnConfig);
      setSearchColumnsVisibility(columns);
    }
  }, [searchFilters]);

  useEffect(() => {
    if (hasChangedColumnVisibility && !isFilterVisibilityDirty) setFilterVisibilityDirty(true);
  }, [hasChangedColumnVisibility]);

  const resetBySelectedVariant = (selectedVariant: SearchFilterDetailsFragment) => {
    const newFormValues = { ...defaultFilterValues };
    const newFilterVisibility = Object.assign({}, filtersVisibility);
    const isSessionVariantSelected = sessionVariant?.id === selectedVariant.id;
    const sessionVariantToApply = isSessionVariantSelected && sessionVariant;
    const currentFilters = sessionVariantToApply || selectedVariant;
    const searchFiltersById =
      currentFilters.filters.reduce((acc, filter) => {
        acc[filter.key] = filter;
        return acc;
      }, {} as Record<string, SearchFilterFieldDetailsFragment>) || {};
    Object.keys(newFilterVisibility).forEach((k) => {
      const key = k as BaseQueryFiltersKeys;
      if (searchFiltersById[key]) {
        newFilterVisibility[key] = searchFiltersById[key].enabled;
        if (internal || !isInternalUserOnly[key]) {
          const filterValue = getFilterValue(
            searchFiltersById[key].key,
            searchFiltersById[key].value
          );
          if (key === 'distributed' && typeof filterValue === 'boolean') {
            newFormValues[key] = filterValue;
          }
          if (
            (key === 'status' ||
              key === 'implementationStatus' ||
              key === 'implementationLanguage' ||
              key === 'issueType') &&
            isArray(filterValue)
          ) {
            newFormValues[key] = filterValue;
          }
          if (
            key !== 'status' &&
            key !== 'implementationStatus' &&
            key !== 'distributed' &&
            key !== 'issueType' &&
            typeof filterValue === 'string'
          ) {
            newFormValues[key] = filterValue;
          }
        }
      } else {
        newFilterVisibility[key] = false;
      }
    });

    return {
      newFormValues,
      newFilterVisibility,
      isSessionVariantSelected
    };
  };

  const resetBySessionVariantOrSelectedVariant = () => {
    let filtersVisibilityToApply;
    let filterVisibilityDirtyToApply = false;
    let columnConfigToApply;

    if (selectedVariant) {
      const { newFormValues, newFilterVisibility, isSessionVariantSelected } =
        resetBySelectedVariant(selectedVariant);

      form.reset(newFormValues);
      filtersVisibilityToApply = newFilterVisibility;
      columnConfigToApply = selectedVariant.columnConfig || getColumnConfig(internal);
      filterVisibilityDirtyToApply = isSessionVariantSelected ? dirtyState : false;
    } else {
      filtersVisibilityToApply = defaultFiltersVisibility;
      columnConfigToApply = getColumnConfig(internal);
      form.reset(defaultFilterValues);
    }

    const columns = getSearchColumnVisibility(columnConfigToApply);
    setSearchColumnsVisibility(columns);
    setFiltersVisibility(filtersVisibilityToApply);
    setFilterVisibilityDirty(filterVisibilityDirtyToApply);
  };

  const previousSelectedVariantFilters = usePrevious(selectedVariant?.filters);

  useEffect(() => {
    resetBySessionVariantOrSelectedVariant();
    if (
      selectedVariant !== null &&
      !isEqual(previousSelectedVariantFilters, selectedVariant.filters)
    ) {
      onGo(false);
    }
  }, [selectedVariant]);

  const getSessionVariantOrDefaultView = (): SearchFilterDetailsFragment => {
    const standardView = getStandardView(internal);

    if ((shouldUseSessionVariant && sessionVariant) || selectedVariant) {
      return sessionVariant || selectedVariant || standardView;
    }

    const defaultView = searchFilters?.find((sf) => sf.isDefault);

    return defaultView || standardView;
  };

  const saveSessionVariant = () => {
    const searchFilter = searchFilters?.find((filters) => filters.id === selectedVariant?.id);

    if (!searchFilter) {
      return;
    }

    const newSessionVariant = {
      ...searchFilter,
      filters: getSearchFilters(),
      columnConfig: searchColumnsVisibility
    };

    if (!isEqual(newSessionVariant, sessionVariant)) {
      setSessionVariant(newSessionVariant);
      setDirtyState(true);
    }
  };

  const handleSubmitValid = (
    formValues: Record<keyof BaseQueryFilters, string | string[] | boolean>,
    isUserInteraction?: boolean
  ) => {
    const filters: BaseQueryFilters = {
      search: formValues.search as string,
      targetLanguage: formValues.targetLanguage as string,
      status: formValues.status as Status[],
      project: formValues.project as string,
      createdBy: formValues.createdBy as string,
      createdAt: formatDateRange(formValues.createdAt as string),
      qualityExpert: formValues.qualityExpert as string,
      projectManager: formValues.projectManager as string,
      queryResponsible: formValues.queryResponsible as string,
      assignee: formValues.assignee as string,
      answerer: formValues.answerer as string,
      translationType: formValues.translationType as TranslationType,
      xtmProjectName: formValues.xtmProjectName as string,
      filename: formValues.filename as string,
      submitterGroupName: formValues.submitterGroupName as string,
      loio: formValues.loio as string,
      repository: formValues.repository as string,
      collection: formValues.collection as string,
      stringId: formValues.stringId as string,
      distributed: formValues.distributed as boolean,
      queryTrackerId: formValues.queryTrackerId as string,
      implementationStatus: formValues.implementationStatus as DistributionStatus[],
      implementationLanguage: formValues.implementationLanguage as string[],
      account: formValues.account as string,
      applicationComponent: formValues.applicationComponent as string,
      substitutePm: formValues.substitutePm as string,
      issueType: formValues.issueType as IssueType[],
      sourceContact: formValues.sourceContact as string
    };
    sanitizeFilters(filters);
    onFilter(filters, isUserInteraction);

    if (isUserInteraction) {
      saveSessionVariant();
    }
  };

  const handleSubmitWithErrors = (errors: Partial<FieldErrorsImpl<typeof defaultFilterValues>>) => {
    // TODO handle?
    console.log(errors);
  };

  const onGo = (isUserInteraction = true) => {
    form.handleSubmit(
      (formValues: Record<keyof BaseQueryFilters, string | string[] | boolean>) =>
        handleSubmitValid(formValues, isUserInteraction),
      handleSubmitWithErrors
    )();
  };

  const sanitizeFilters = (filters: BaseQueryFilters) => {
    Object.entries(filters).forEach(([key, value]) => {
      if (
        value?.toString().trim() === '' ||
        value === allOptions ||
        value === notSelected ||
        (isArray(value) && value.length === 0) ||
        (key === 'distributed' && value === false)
      ) {
        // @ts-ignore
        delete filters[key];
      }
    });
  };

  const onClear = () => {
    setFilterVisibilityDirty(true);
    form.reset(defaultFilterValues);
  };

  const onFiltersDialogOpen = () => {
    const formValues = form.getValues();
    setOpenDialogFilters(formValues);
    setIsSearchRequired(true);
  };

  const onCancel = () => {
    Object.entries(openDialogFilters).forEach(([key, value]) => {
      form.setValue(key as BaseQueryFiltersKeys, value);
    });
  };

  const onFiltersDialogSave = (
    e: CustomEvent<{
      elements: Record<string, HTMLElement>;
      toggledElements?: Record<string, HTMLElement>;
      filters: HTMLElement[];
      search: HTMLElement;
    }>
  ) => {
    if (e.detail.toggledElements) {
      const newFilterSettings: FiltersVisibility = Object.assign({}, filtersVisibility);
      const filterInput = e.detail.toggledElements;
      Object.keys(filtersVisibility).forEach((key: string) => {
        // Sets the current filter (e.g. Project, Target Language, Status) visibility as true or false
        const k = key as BaseQueryFiltersKeys;
        const isCurrentFilterVisible = filterInput[eventFilterKeys[k]];
        newFilterSettings[k] = !!isCurrentFilterVisible;

        // Reverts current filter value to default if visibility is false
        if (!isCurrentFilterVisible) {
          const defaultFilterValue = defaultFilterValues[k];
          form.setValue(k, defaultFilterValue, {
            shouldDirty: true,
            shouldValidate: false,
            shouldTouch: false
          });
        }
      });
      if (!isEqual(filtersVisibility, newFilterSettings)) {
        setFilterVisibilityDirty(true);
        setFiltersVisibility(newFilterSettings);
      }
    }
    return Promise.resolve();
  };

  const getSearchFilters = () => {
    return Object.keys(eventFilterKeys).map((k) => {
      const key = k as BaseQueryFiltersKeys;
      let value = form.getValues(key);
      if (typeof value !== 'string') value = value.toString();
      return {
        key,
        value,
        enabled: filtersVisibility[key]
      };
    });
  };

  const handleSaveAs = (e: { detail: SelectedVariant }) => {
    return callCreateSearchFilter({
      input: {
        name: e.detail.children,
        isDefault: e.detail.isDefault || false,
        favorite: e.detail.favorite || false,
        filters: getSearchFilters(),
        columnConfig: searchColumnsVisibility
      }
    }).then((newSelected) => {
      if (newSelected.data?.mutation) {
        setSelectedVariant(newSelected.data?.mutation);
      }
      setFilterVisibilityDirty(false);
      setHasChangedColumnVisibility(false);
      setDirtyState(false);
    });
  };

  const handleOnSave = (e: { detail: SelectedVariant }) => {
    if (!selectedVariant || selectedVariant?.id === '') {
      return null;
    }
    return callUpdateOneSearchFilters({
      input: {
        id: selectedVariant?.id,
        name: e.detail.children,
        isDefault: e.detail.isDefault || false,
        favorite: e.detail.favorite || false,
        filters: getSearchFilters(),
        columnConfig: searchColumnsVisibility,
        isSubscribed: selectedVariant?.isSubscribed || false
      }
    }).then(() => {
      setFilterVisibilityDirty(false);
      setHasChangedColumnVisibility(false);
      setDirtyState(false);
    });
  };

  const handleSaveManageViews = async (e: {
    detail: {
      deletedVariants: VariantItemPropTypes[];
      prevVariants: VariantItemPropTypes[];
      updatedVariants: (SelectedVariant & {
        prevVariant?: VariantItemPropTypes;
      })[];
      variants: SelectedVariant[];
    };
  }) => {
    await new Promise((resolve) => setTimeout(resolve, 50));
    const searchFiltersByName =
      searchFilters?.reduce((acc, filter) => {
        acc[filter.name] = filter;
        return acc;
      }, {} as Record<string, SearchFilterDetailsFragment>) || {};
    const updateSearchFilter: SearchFilterUpdate[] = e.detail.updatedVariants
      .reduce((acc, variant) => {
        const name = variant.prevVariant?.children;
        if (!name || !searchFiltersByName[name]) {
          return acc;
        }
        const searchFilter = { ...searchFiltersByName[name] };
        delete searchFilter.__typename;
        acc.push({
          ...searchFilter,
          isDefault: variant.isDefault || false,
          favorite: variant.favorite || false,
          name: variant.children,
          filters: searchFilter.filters.map((filter) => {
            const copyFilter = { ...filter };
            delete copyFilter.__typename;
            return copyFilter;
          }),
          columnConfig: searchColumnsVisibility
        });
        return acc;
      }, [] as SearchFilterUpdate[])
      .filter((searchFilter) => {
        return searchFilter.id !== STANDARD_VIEW_ID;
      });
    const deleteSearchFilter: SearchFilterDelete[] = e.detail.deletedVariants.map((variant) => {
      const id = searchFiltersByName[variant.children].id;
      if (selectedVariant?.id === id) {
        setSelectedVariant(null);
      }
      return {
        id
      };
    });

    callUpdateMultipleSearchFilters({
      update: updateSearchFilter,
      delete: deleteSearchFilter
    }).then(() => {
      const defaultView = updateSearchFilter.find((variant) => variant.isDefault);

      setDefaultViewPreferenceId(defaultView?.id || STANDARD_VIEW_ID);

      setFilterVisibilityDirty(false);
      setHasChangedColumnVisibility(false);
      setDirtyState(false);
    });
  };

  const handleChangeSelect = (
    e: Ui5CustomEvent<
      HTMLElement,
      {
        selectedVariant: SelectedVariant;
      }
    >
  ): void => {
    const newSelection =
      searchFilters?.find((sf) => sf.name === e.detail.selectedVariant.children) || null;
    setSelectedVariant(newSelection);
    setHasChangedColumnVisibility(false);
  };

  return {
    form,
    focus,
    setFocus,
    clearFocus,
    searchFilters,
    projectNames,
    isSearchRequired,
    fetchingSearchQueries,
    selectedVariant,
    filtersVisibility,
    isFilterVisibilityDirty,
    allOptions,
    notSelected,
    setIsSearchRequired,
    onGo,
    onClear,
    onCancel,
    onFiltersDialogOpen,
    onFiltersDialogSave,
    handleSaveAs,
    handleSaveManageViews,
    handleOnSave,
    handleChangeSelect
  };
};
